import { Menu as BaseMenu } from "@base-ui/react/menu";
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils/cn";

const popupClasses =
  "min-w-[8rem] origin-[var(--transform-origin)] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0";

const DropdownMenuRoot = ({ ...props }: React.ComponentProps<typeof BaseMenu.Root>) => {
  return <BaseMenu.Root {...props} />;
};

const DropdownMenuTrigger = ({ ...props }: React.ComponentProps<typeof BaseMenu.Trigger>) => {
  return <BaseMenu.Trigger data-slot="dropdown-menu-trigger" {...props} />;
};

type DropdownMenuContentProps =
  & React.ComponentProps<typeof BaseMenu.Popup>
  & Pick<
    React.ComponentProps<typeof BaseMenu.Positioner>,
    "side" | "align" | "sideOffset" | "alignOffset"
  >;

const DropdownMenuContent = ({
  className,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  alignOffset,
  ...props
}: DropdownMenuContentProps) => {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        className="z-50"
        side={side}
        align={align}
        sideOffset={sideOffset}
        {...(alignOffset !== undefined && { alignOffset })}
      >
        <BaseMenu.Popup
          data-slot="dropdown-menu-content"
          className={cn(popupClasses, className)}
          {...props}
        />
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
};

const DropdownMenuGroup = ({ ...props }: React.ComponentProps<typeof BaseMenu.Group>) => {
  return <BaseMenu.Group data-slot="dropdown-menu-group" {...props} />;
};

const DropdownMenuItem = ({
  className,
  inset,
  variant = "default",
  ...props
}: React.ComponentProps<typeof BaseMenu.Item> & {
  inset?: boolean;
  variant?: "default" | "destructive";
}) => {
  return (
    <BaseMenu.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-red-500 data-[variant=destructive]:data-[highlighted]:bg-destructive/10 data-[variant=destructive]:data-[highlighted]:text-destructive dark:data-[variant=destructive]:text-red-400 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};

const DropdownMenuCheckboxItem = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof BaseMenu.CheckboxItem>) => {
  return (
    <BaseMenu.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "relative flex cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <BaseMenu.CheckboxItemIndicator>
          <CheckIcon className="size-4" />
        </BaseMenu.CheckboxItemIndicator>
      </span>
      {children}
    </BaseMenu.CheckboxItem>
  );
};

const DropdownMenuRadioGroup = ({ ...props }: React.ComponentProps<typeof BaseMenu.RadioGroup>) => {
  return <BaseMenu.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
};

const DropdownMenuRadioItem = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof BaseMenu.RadioItem>) => {
  return (
    <BaseMenu.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "relative flex cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <BaseMenu.RadioItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </BaseMenu.RadioItemIndicator>
      </span>
      {children}
    </BaseMenu.RadioItem>
  );
};

// Standalone label header (not group-scoped). Base UI's GroupLabel requires a
// Menu.Group ancestor; our Label is used standalone, so render a styled div.
const DropdownMenuLabel = ({
  className,
  inset,
  ...props
}: React.ComponentProps<"div"> & {
  inset?: boolean;
}) => {
  return (
    <div
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn("px-2 py-1.5 text-sm font-semibold data-[inset]:pl-8", className)}
      {...props}
    />
  );
};

const DropdownMenuSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseMenu.Separator>) => {
  return (
    <BaseMenu.Separator
      data-slot="dropdown-menu-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
};

const DropdownMenuShortcut = ({ className, ...props }: React.ComponentProps<"span">) => {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  );
};

const DropdownMenuSub = ({ ...props }: React.ComponentProps<typeof BaseMenu.SubmenuRoot>) => {
  return <BaseMenu.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
};

const DropdownMenuSubTrigger = ({
  children,
  className,
  inset,
  ...props
}: React.ComponentProps<typeof BaseMenu.SubmenuTrigger> & {
  inset?: boolean;
}) => {
  return (
    <BaseMenu.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[inset]:pl-8 data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </BaseMenu.SubmenuTrigger>
  );
};

type DropdownMenuSubContentProps =
  & React.ComponentProps<typeof BaseMenu.Popup>
  & Pick<React.ComponentProps<typeof BaseMenu.Positioner>, "side" | "align" | "sideOffset">;

const DropdownMenuSubContent = ({
  className,
  side = "right",
  align = "start",
  sideOffset = 0,
  ...props
}: DropdownMenuSubContentProps) => {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner className="z-50" side={side} align={align} sideOffset={sideOffset}>
        <BaseMenu.Popup
          data-slot="dropdown-menu-sub-content"
          className={cn(popupClasses, "shadow-lg", className)}
          {...props}
        />
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
};

export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
  Group: DropdownMenuGroup,
  Item: DropdownMenuItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioGroup: DropdownMenuRadioGroup,
  RadioItem: DropdownMenuRadioItem,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
});
